import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type ArrivalWindowSelectProps = FieldWrapperInjectedProps & {
  /** Hours as a string — `"0.5"` … `"8"`. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  "data-test"?: string;
};

const ARRIVAL_WINDOWS = ["0.5", "1", "2", "3", "4", "5", "6", "7", "8"];

export const formatArrivalWindowLabel = (arrivalWindow: string) => {
  return arrivalWindow == "0.5" ? "30m" : `${arrivalWindow}h`;
};

export default function ArrivalWindowSelect({
  className,
  value,
  onChange,
  disabled,
  defaultOpen = false,
  onOpenChange,
  ...props
}: ArrivalWindowSelectProps) {
  const [open, toggleOpen] = useToggle(defaultOpen);
  const options = ARRIVAL_WINDOWS;
  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    toggleOpen(open);
  };
  return (
    <Popover modal={true} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger disabled={disabled} asChild>
        <div
          role="combobox"
          className={cn(
            /* sorted */
            "flex",
            "items-center justify-between",
            "w-full h-10",
            "px-3 py-2",
            "border border-border-dark-low rounded-lg",
            "aria-invalid:border-border-negative-max aria-invalid:data-[state=open]:border-border-negative-max data-[state=open]:border-border-dark-max data-[state=open]:border-[1.5px]",
            !value?.length && "text-text-dark-min",
            className,
          )}
          {...props}
        >
          {value ? (
            <p className="text-text-dark-high text-sm font-medium">
              {formatArrivalWindowLabel(value)}
            </p>
          ) : (
            "Select type"
          )}
          <BwChevronDownLine
            size={16}
            className="text-text-dark-high shrink-0"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandList>
            <CommandGroup>
              {options.map((option: string) => (
                <CommandItem
                  value={option}
                  key={option}
                  onSelect={(e) => {
                    onChange(e);
                    handleOpenChange(false);
                  }}
                >
                  <p className="grow">{formatArrivalWindowLabel(option)}</p>
                  <BwCheckmarkLine
                    size={16}
                    className={cn(
                      /* sorted */
                      "shrink-0",
                      value === option ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
